import { Grid, GridItem } from '@chakra-ui/react';
import {
  Button,
  Icons,
  Notification,
  PassiveVariants,
  SvgSpriteConsumer,
} from '@pluxee-design-system/core';
import { InputControl } from '@pluxee-design-system/react-hook-form';
import { SelectControl } from 'common/forms';
import { I18nProps, withTranslations } from 'i18n';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ConfigField } from 'types/config';
import { BelgiumOnboardingValues, TerminalProviders } from '../types';
import { findTerminalProviderByTID } from '../utils';

interface TerminalRowProps extends I18nProps {
  config: Record<string, ConfigField>;
  idPrefix: string;
  index: number;
  name: string;
  onRemove: (index: number) => void;
  providers: TerminalProviders;

  // Based on acquirer
  canRemove?: boolean;
  isMandatory?: boolean;
}

const TerminalRow = ({
  canRemove = true,
  config,
  idPrefix,
  index,
  isMandatory,
  name,
  onRemove,
  providers: { providers, tids },
  t,
}: TerminalRowProps) => {
  const { formState, setValue, watch } = useFormContext<BelgiumOnboardingValues>();
  const [valuesTerminalId] = watch([`${name}terminalId` as 'POS.0.terminalId']);

  const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);
  const handleProvider = useMemo(
    () =>
      debounce(
        (value: string) =>
          setValue(`${name}terminalProvider` as 'POS.0.terminalId', value, {
            shouldTouch: true,
            shouldDirty: true,
            shouldValidate: true,
          }),
        200,
      ),
    [name, setValue],
  );
  const hasTerminalIdError = Boolean(formState.errors?.POS?.[index]?.terminalId);
  const columnsSize = config.terminalId.visible ? (config.terminalProvider.visible ? 2 : 1) : 0;
  const showTerminalIdNotification = useMemo(
    () =>
      !isMandatory && valuesTerminalId === ''
        ? false
        : !hasTerminalIdError && !Boolean(findTerminalProviderByTID(valuesTerminalId, tids)),
    [isMandatory, hasTerminalIdError, tids, valuesTerminalId],
  );

  useEffect(() => {
    handleProvider(findTerminalProviderByTID(valuesTerminalId, tids));
  }, [handleProvider, tids, valuesTerminalId]);

  return (
    <Grid
      key={`POS-field-${index}`}
      templateAreas={{
        base: `"input${canRemove ? ' button' : ''}"
        "select${canRemove ? ' button' : ''}"`,
        md: `"input select${canRemove ? ' button' : ''}"`,
      }}
      templateColumns={{
        base: `1fr${canRemove ? ' 70px' : ''}`,
        md: `repeat(${columnsSize}, 1fr)${canRemove ? ' 70px' : ''}`,
      }}
      gap={4}
    >
      {config.terminalId.visible && (
        <GridItem width="100%" area="input">
          <InputControl
            type="text"
            mandatory={isMandatory}
            name={`${name}terminalId`}
            id={`${idPrefix}${name}terminalId`}
            placeholder={t('location_add.form.terminalId.label', 'Terminal ID')}
            title={t('location_add.form.terminalId.label', 'Terminal ID')}
          />
        </GridItem>
      )}

      {config.terminalProvider.visible && (
        <GridItem width="100%" area="select">
          <SelectControl
            mandatory={isMandatory}
            disabled={!showTerminalIdNotification}
            name={`${name}terminalProvider`}
            id={`${idPrefix}${name}terminalProvider`}
            placeholder={t('location_add.form.terminalProviderOnline.label', 'Provider')}
            title={t('location_add.form.terminalProviderOnline.label', 'Provider')}
            notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
            options={providers}
          />
        </GridItem>
      )}
      {canRemove && (
        <GridItem width="70px" mt={7} area="button">
          <Button
            type="button"
            onClick={handleRemove}
            variant="secondaryTextOnly"
            id={`${idPrefix}${name}deleteButton`}
            size="md"
            alignSelf="flex-end"
          >
            <SvgSpriteConsumer size={24} svgId={Icons.TRASH24} />
          </Button>
        </GridItem>
      )}
      {showTerminalIdNotification && (
        <GridItem colStart={1} colEnd={{ base: 2, md: columnsSize + 1 }}>
          <Notification
            description={t('location_add.form.terminalId.informativeMessage', '12323')}
            variant={PassiveVariants.PASSIVE_INFORMATION}
            isClosable={false}
            withoutShadow
          />
        </GridItem>
      )}
    </Grid>
  );
};

export default withTranslations(TerminalRow);
